import { useCallback, useEffect, useState } from 'react';
import { api, apiFetch, tokenStore, type ApiError } from '../../api/client';
import { Alert } from '../../components/Field';
import { Tile } from '../../components/Tile';
import { money, relativeTime, titleCase } from '../../lib/format';

interface Dashboard {
  generatedAt: string;
  users: { total: number; active: number; inactive: number; registeredLast30Days: number };
  customers: { total: number; active: number };
  subscriptions: { active: number; expired: number };
  courses: { total: number; published: number; draft: number };
  learning: {
    enrolments: number;
    completed: number;
    completionRate: number;
    certificatesIssued: number;
    learnersActiveLast30Days: number;
    totalLearningHours: number;
  };
  commerce: {
    paidOrders: number;
    grossMinor: number;
    refundedMinor: number;
    netMinor: number;
    currency: string;
    couponRedemptions: number;
    averageOrderMinor: number;
  };
  mostPopularCourses: Array<{
    id: string;
    title: string;
    enrolments: number;
    rating: number;
    ratingCount: number;
  }>;
}

interface TableReport {
  title: string;
  columns: Array<{ key: string; label: string; type?: string }>;
  rows: Array<Record<string, string | number | boolean | null>>;
}

interface Business {
  currency: string;
  revenueByMonth: Array<{
    month: string;
    orders: number;
    grossMinor: number;
    refundedMinor: number;
    netMinor: number;
  }>;
  subscriptionsByPlan: Array<{ plan: string; count: number }>;
  renewalRate: number;
  coupons: Array<{ code: string; redemptions: number; remaining: number | null; status: string }>;
}

interface Scores {
  total: number;
  passed: number;
  passRate: number;
  buckets: Array<{ label: string; attempts: number }>;
}

type Tab = 'overview' | 'courses' | 'business' | 'organizations';

/**
 * Platform analytics (BRD FR-RPT-001 to FR-RPT-009).
 *
 * The freshness timestamp is shown next to the figures rather than hidden in a
 * tooltip: the dashboard is served from a cache, and FR-RPT-009 requires the
 * reader to know how old what they are looking at is.
 */
export function Reports() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [courses, setCourses] = useState<TableReport | null>(null);
  const [scores, setScores] = useState<Scores | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [organizations, setOrganizations] = useState<TableReport | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const loadDashboard = useCallback((refresh = false) => {
    setBusy(true);
    api<Dashboard>(`/admin/reports/dashboard${refresh ? '?refresh=true' : ''}`)
      .then(setDashboard)
      .catch((caught) => setError(caught as ApiError))
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    if (tab === 'overview' && !dashboard) loadDashboard();
    if (tab === 'courses' && !courses) {
      api<TableReport>('/admin/reports/courses').then(setCourses).catch(() => undefined);
      api<Scores>('/admin/reports/courses/scores').then(setScores).catch(() => undefined);
    }
    if (tab === 'business' && !business) {
      api<Business>('/admin/reports/business').then(setBusiness).catch(() => undefined);
    }
    if (tab === 'organizations' && !organizations) {
      api<TableReport>('/admin/reports/organizations').then(setOrganizations).catch(() => undefined);
    }
  }, [tab, dashboard, courses, business, organizations, loadDashboard]);

  /**
   * Download an export.
   *
   * Fetched with the access token rather than followed as a plain link, because
   * a bare anchor cannot send an Authorization header and this API has no
   * ambient cookie authority.
   */
  async function download(report: string, format: 'csv' | 'xlsx') {
    setError(null);
    try {
      const response = await apiFetch(`/admin/reports/${report}/export?format=${format}`, {
        headers: { Authorization: `Bearer ${tokenStore.access ?? ''}` },
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as Partial<ApiError>;
        throw { ...payload, status: response.status } as ApiError;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${report}.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Reports &amp; analytics</h1>
        <p className="page__lede">
          Figures across the whole platform. A client company sees only its own people, on its
          Company Overview.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}

      <div className="tabs" role="tablist">
        {(['overview', 'courses', 'business', 'organizations'] as Tab[]).map((name) => (
          <button
            key={name}
            role="tab"
            aria-selected={tab === name}
            className={`tabs__tab${tab === name ? ' tabs__tab--on' : ''}`}
            onClick={() => setTab(name)}
          >
            {name === 'organizations' ? 'Clients' : titleCase(name)}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        !dashboard ? (
          <div className="spinner">Loading…</div>
        ) : (
          <>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <span className="muted">
                Figures computed {relativeTime(dashboard.generatedAt)}. They may be cached for up to
                two minutes.
              </span>
              <button
                className="btn btn--quiet btn--sm"
                onClick={() => loadDashboard(true)}
                disabled={busy}
              >
                {busy ? 'Recomputing…' : 'Recompute now'}
              </button>
            </div>

            <div className="tiles">
              <Tile
                tone="azure"
                icon="◍"
                label="People"
                value={dashboard.users.total.toLocaleString()}
                note={`${dashboard.users.active} active`}
              />
              <Tile
                tone="azure"
                icon="↗"
                label="Registered (30d)"
                value={dashboard.users.registeredLast30Days.toLocaleString()}
                note={`${dashboard.learning.learnersActiveLast30Days} learning`}
              />
              <Tile
                tone="navy"
                icon="⬢"
                label="Clients"
                value={dashboard.customers.total.toLocaleString()}
                note={`${dashboard.customers.active} active`}
              />
              <Tile
                tone="indigo"
                icon="↻"
                label="Subscriptions"
                value={dashboard.subscriptions.active.toLocaleString()}
                note={`${dashboard.subscriptions.expired} expired`}
              />
              <Tile
                tone="navy"
                icon="▦"
                label="Courses"
                value={dashboard.courses.published.toLocaleString()}
                note={`${dashboard.courses.draft} in draft`}
              />
              <Tile
                tone="teal"
                icon="▶"
                label="Enrolments"
                value={dashboard.learning.enrolments.toLocaleString()}
                note={`${dashboard.learning.completionRate}% completed`}
                progress={dashboard.learning.completionRate}
              />
              <Tile
                tone="gold"
                icon="✦"
                label="Certificates"
                value={dashboard.learning.certificatesIssued.toLocaleString()}
                note={`${dashboard.learning.totalLearningHours.toLocaleString()} learning hours`}
              />
              <Tile
                tone="gold"
                icon="₹"
                label="Net revenue"
                value={money(dashboard.commerce.netMinor, dashboard.commerce.currency)}
                note={`${dashboard.commerce.paidOrders} paid orders`}
              />
            </div>

            <div className="card" style={{ marginTop: 20 }}>
              <div className="card__head">
                <span className="card__title">Most popular courses</span>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th style={{ width: 120 }}>Enrolments</th>
                    <th style={{ width: 120 }}>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.mostPopularCourses.map((course) => (
                    <tr key={course.id}>
                      <td>{course.title}</td>
                      <td>{course.enrolments}</td>
                      <td>
                        {course.ratingCount > 0
                          ? `${course.rating.toFixed(1)} (${course.ratingCount})`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : null}

      {tab === 'courses' ? (
        <>
          {scores ? (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card__head">
                <span className="card__title">
                  Assessment scores — {scores.passRate}% pass rate across {scores.total} attempts
                </span>
              </div>
              <div className="card__body">
                <div className="hist">
                  {scores.buckets.map((bucket) => {
                    const tallest = Math.max(...scores.buckets.map((b) => b.attempts), 1);
                    return (
                      <div className="hist__col" key={bucket.label}>
                        <div
                          className="hist__bar"
                          style={{ height: `${(bucket.attempts / tallest) * 100}%` }}
                          title={`${bucket.attempts} attempts`}
                        />
                        <span className="hist__label">{bucket.label}</span>
                        <span className="hist__value">{bucket.attempts}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
          <ReportTable report={courses} onExport={(format) => void download('courses', format)} />
        </>
      ) : null}

      {tab === 'business' ? (
        !business ? (
          <div className="spinner">Loading…</div>
        ) : (
          <>
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card__head">
                <span className="card__title">Revenue by month</span>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    className="btn btn--quiet btn--sm"
                    onClick={() => void download('revenue', 'csv')}
                  >
                    CSV
                  </button>
                  <button
                    className="btn btn--quiet btn--sm"
                    onClick={() => void download('revenue', 'xlsx')}
                  >
                    Excel
                  </button>
                </div>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Paid orders</th>
                    <th>Gross</th>
                    <th>Refunded</th>
                    <th>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {business.revenueByMonth.map((row) => (
                    <tr key={row.month}>
                      <td>{row.month}</td>
                      <td>{row.orders}</td>
                      <td>{money(row.grossMinor, business.currency)}</td>
                      <td>
                        {row.refundedMinor > 0
                          ? money(row.refundedMinor, business.currency)
                          : '—'}
                      </td>
                      <td>
                        <strong>{money(row.netMinor, business.currency)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row" style={{ gap: 20, alignItems: 'flex-start' }}>
              <div className="card" style={{ flex: 1 }}>
                <div className="card__head">
                  <span className="card__title">
                    Subscriptions by plan — {business.renewalRate}% set to renew
                  </span>
                </div>
                <table className="table">
                  <tbody>
                    {business.subscriptionsByPlan.map((row) => (
                      <tr key={row.plan}>
                        <td>{row.plan}</td>
                        <td style={{ width: 80 }}>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="card" style={{ flex: 1 }}>
                <div className="card__head">
                  <span className="card__title">Coupon usage</span>
                </div>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Redeemed</th>
                      <th>Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {business.coupons.map((coupon) => (
                      <tr key={coupon.code}>
                        <td>
                          {coupon.code}
                          {coupon.status !== 'ACTIVE' ? (
                            <span className="pill pill--quiet" style={{ marginLeft: 6 }}>
                              {titleCase(coupon.status)}
                            </span>
                          ) : null}
                        </td>
                        <td>{coupon.redemptions}</td>
                        <td>{coupon.remaining === null ? 'unlimited' : coupon.remaining}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )
      ) : null}

      {tab === 'organizations' ? (
        <ReportTable
          report={organizations}
          onExport={(format) => void download('organizations', format)}
        />
      ) : null}
    </>
  );
}

/** Renders any declared report table, so a new report needs no new component. */
function ReportTable({
  report,
  onExport,
}: {
  report: TableReport | null;
  onExport: (format: 'csv' | 'xlsx') => void;
}) {
  if (!report) return <div className="spinner">Loading…</div>;

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">
          {report.title} ({report.rows.length})
        </span>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--quiet btn--sm" onClick={() => onExport('csv')}>
            CSV
          </button>
          <button className="btn btn--quiet btn--sm" onClick={() => onExport('xlsx')}>
            Excel
          </button>
        </div>
      </div>

      {report.rows.length === 0 ? (
        <div className="empty">Nothing to report yet.</div>
      ) : (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                {report.columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row, index) => (
                <tr key={index}>
                  {report.columns.map((column) => (
                    <td key={column.key}>{cell(row[column.key], column.type)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function cell(value: unknown, type?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'percent' && typeof value === 'number') return `${value}%`;
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return titleCase(String(value)) === String(value) ? String(value) : String(value);
}
